import type { GitConnection, GitConnectionKind, GitConnectionStatus } from "~/api/gitTypes";
import type { StatusTone } from "~/features/git/parts";

type Translate = (key: string) => string;

/** `owner/repo` for a remote, the last path segment for a mount — what a person calls it. */
export function repositoryName(connection: Pick<GitConnection, "remoteUrl" | "kind">): string {
  const url = connection.remoteUrl.replace(/\.git$/i, "").replace(/\/+$/, "");
  const segments = url.split(/[/:]/).filter(Boolean);
  if (connection.kind === "mount") {
    return segments[segments.length - 1] ?? url;
  }
  return segments.slice(-2).join("/") || url;
}

/** The name, then the subtree when the connection is one: `knowIt / documentation/ai-map`. */
export function repositoryLabel(connection: GitConnection): string {
  const name = repositoryName(connection);
  return connection.pathPrefix ? `${name} / ${connection.pathPrefix}` : name;
}

/** Literal `t()` calls, one per case, so the extractor sees every label (the house discipline). */
export function kindLabel(kind: GitConnectionKind, t: Translate): string {
  switch (kind) {
    case "public":
      return t("Public repository");
    case "app":
      return t("GitHub App");
    case "deploy-key":
      return t("Deploy key");
    case "pat":
      return t("Access token");
    default:
      return t("Local mount");
  }
}

export function kindHint(kind: GitConnectionKind, t: Translate): string {
  switch (kind) {
    case "public":
      return t(
        "Read over the provider's API with no credential at all. Rate-limited, but enough for hourly history.",
      );
    case "app":
      return t(
        "A GitHub App installation: only the installation id is stored — tokens are minted per use and expire within the hour.",
      );
    case "deploy-key":
      return t(
        "An SSH clone credential. Accepted, but history is read over the API, so it cannot be used yet.",
      );
    case "pat":
      return t(
        "A fine-grained, read-only, single-repository token — sealed on the way in, never shown again. The fallback, not the plan.",
      );
    default:
      return t(
        "A read-only path already mounted into the server's container. Nothing to steal, nothing to rotate.",
      );
  }
}

export function statusLabel(status: GitConnectionStatus, t: Translate): string {
  switch (status) {
    case "ok":
      return t("Healthy");
    case "failing":
      return t("Failing");
    case "disabled":
      return t("Disabled");
    default:
      return t("Not ingested yet");
  }
}

export const STATUS_TONE: Record<GitConnectionStatus, StatusTone> = {
  new: "neutral",
  ok: "success",
  failing: "danger",
  disabled: "neutral",
};

/** Bytes as a person reads them; the listing is a menu, not a ledger. */
export function formatSize(bytes: number | null): string {
  if (bytes === null) {
    return "";
  }
  if (bytes < 1024) {
    return `${bytes} B`;
  }
  if (bytes < 1024 * 1024) {
    return `${(bytes / 1024).toFixed(bytes < 10 * 1024 ? 1 : 0)} KB`;
  }
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/** Parent of a connection-relative path; "" at the root. */
export function parentPath(path: string): string {
  const index = path.lastIndexOf("/");
  return index < 0 ? "" : path.slice(0, index);
}
